import type { BlobLike } from "@pdfking/compatibility"
import { BlobArray } from "@pdfking/compatibility"
import {
  type PdfiumApi,
  PdfiumExports as E,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type {
  PageNumber,
  PDFMergeFunction,
  PDFMergeInput,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { ReadDocument } from "../functions/ReadDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

// PDF header + Catalog (using \r\n like pdfium)
const PDF_HEADER =
  "%PDF-1.5\r\n1 0 obj <</Type/Catalog/Pages 2 0 R>> endobj\r\n"

// Offset of object 1 (Catalog) in header: "%PDF-1.5\r\n" = 10 bytes
const CATALOG_OFFSET = 10

// Flush threshold for C++ buffered writes (1MB)
const FLUSH_THRESHOLD = 1024 * 1024

interface PageObjEntry {
  readonly page: PageNumber
  readonly obj: number
}

// Format a single xref entry (20 bytes): "OOOOOOOOOO 00000 n\r\n" (like pdfium)
const formatXrefEntry = (offset: number): string =>
  `${String(offset).padStart(10, "0")} 00000 n\r\n`

async function* PDFMergeImpl(
  api: PdfiumApi,
  pages: AsyncIterable<PDFMergeInput> | Iterable<PDFMergeInput>
): AsyncGenerator<BlobLike> {
  // Yield PDF header and Catalog
  yield BlobArray(PDF_HEADER)

  // Track state
  let lastObjNum = 2 // Catalog is obj 1, Pages is obj 2, pages start at 3
  let documentSize = PDF_HEADER.length
  const pageEntries: PageObjEntry[] = []
  const xrefChunks = BlobArray() // Raw xref bytes from C++

  // Allocate objInfo buffer: [pageObjNum, highestObjNum]
  const { pointer: objInfoPtr, free: freeObjInfo } = api.malloc(8 as Bytes)

  try {
    for await (const input of pages) {
      const reader = await ReadDocument(api, input)

      try {
        const pageCount = await api.call(
          E.FPDF_GetPageCount,
          reader.doc_pointer
        )
        if (pageCount !== 1) {
          throw new Error(`Expected source to have 1 page, got ${pageCount}`)
        }

        // Collect content via callback
        const contentChunks = BlobArray()

        // type=0 content, type=1 xref
        const writeCallback = (
          _: number,
          type: number,
          data: number,
          size: number
        ) => {
          const bytes = api.readBytes(data as Pointer, size as Bytes)
          if (type === 0) {
            contentChunks.push(bytes)
          } else {
            xrefChunks.push(bytes)
          }
          return size
        }

        const { pointer: callbackPtr, free: freeCallback } = api.addCallback(
          writeCallback,
          "iiiii"
        )
        const { pointer: structPtr, free: freeStruct } = api.malloc(8 as Bytes)

        api.setInt32(structPtr, 1)
        api.setInt32((structPtr + 4) as Pointer, callbackPtr)

        try {
          // Export page objects - C++ calls callback for content (type=0) and xref (type=1)
          const bytesWritten = await api.safeCall(
            E.PDFKING_ExportPageObjects,
            reader.doc_pointer,
            0,
            lastObjNum,
            documentSize,
            structPtr,
            objInfoPtr,
            FLUSH_THRESHOLD
          )

          // Yield all content chunks
          yield* contentChunks.blobs

          // Read object info: [pageObjNum, highestObjNum]
          const objInfoData = api.readBytes(objInfoPtr, 8 as Bytes)
          const objInfoView = new DataView(
            objInfoData.buffer,
            objInfoData.byteOffset,
            8
          )
          const newPageObjNum = objInfoView.getUint32(0, true)
          const highestObjNum = objInfoView.getUint32(4, true)

          // Update tracking
          pageEntries.push({ page: input.page_number, obj: newPageObjNum })
          lastObjNum = highestObjNum
          documentSize += bytesWritten
        } finally {
          freeStruct()
          freeCallback()
        }
      } finally {
        await CloseDocument(api, reader)
      }
    }

    // Write footer: Pages dict + xref + trailer
    if (pageEntries.length > 0) {
      const pagesOffset = documentSize
      const sortedPages = pageEntries.sort((a, b) => a.page - b.page)
      const kids = sortedPages.map(e => `${e.obj} 0 R`).join(" ")
      const pagesObj = `2 0 obj <</Type/Pages/Count ${sortedPages.length}/Kids[${kids}]>> endobj\r\n`

      // xref header (objects 0, 1, 2) - using \r\n like pdfium
      yield BlobArray([
        pagesObj,
        `xref\r\n`,
        `0 1\r\n0000000000 65535 f\r\n`,
        `1 ${lastObjNum}\r\n`,
        formatXrefEntry(CATALOG_OFFSET),
        formatXrefEntry(pagesOffset),
      ])

      // xref entries from C++ (already formatted as raw bytes)
      yield* xrefChunks.blobs

      // trailer - using \r\n like pdfium
      yield BlobArray([
        `trailer\r\n<</Size ${lastObjNum + 1}/Root 1 0 R>>\r\n`,
        `startxref\r\n${documentSize + pagesObj.length}\r\n%%EOF\r\n`,
      ])
    }
  } finally {
    freeObjInfo()
  }
}

export const PDFMergeExperimental: PDFMergeFunction = pages =>
  ProvisionPdfium(api => PDFMergeImpl(api, pages))
